// „Läuft gerade": Geräte und Vorgänge, die gerade arbeiten und von denen
// man schnell dorthin springen will (Tim 15.10.2026: „Wie können wir
// anzeigen, wann der Drucker aktiv ist und dann schnell dahin springen?
// Der Drucker wäre das erste Gerät für so ein Feature." -- gewählt:
// Statusleiste + Baum, am Telefon eine Zeile über der Tab-Leiste).
//
// Diese Datei ist rein (Typ und Text, getestet in laeuft.test.mjs); die
// Quelle, die alle Einträge sammelt, ist laeuft.svelte.ts. Ein neues Gerät
// liefert dort einen Laeuft-Eintrag aus seinem Store -- Statusleiste,
// Baum, Aktivitätsleiste und Telefon zeigen ihn dann von selbst.

export interface Laeuft {
  /** stabil, z. B. "drucker" */
  id: string;
  /** kurz: „P1S" */
  name: string;
  /** was es tut: „druckt", „angehalten: Filament leer" */
  was: string;
  /** 0..100, wenn es einen Fortschritt gibt */
  fortschritt?: number;
  /** voraussichtliches Ende (ISO) */
  bis?: string;
  /** wohin der Klick führt */
  pfad: string;
  /** weitere Seiten, an denen es läuft (der Chat einer Konstruktion führt
   *  zu ihr, läuft aber auch in seinem eigenen Tab /claude/<id>) */
  pfade?: string[];
  /** laeuft = Akzent; warn = wartet auf einen Handgriff; leise = pausiert */
  ton: "laeuft" | "warn" | "leise";
  /** Tooltip: der ganze Satz */
  titel: string;
  /** Gleichartiges (mehrere Claude-Chats) steht als EIN Eintrag da:
   *  „Claude 2 arbeiten", der Klick zeigt die Liste (buendle). */
  gruppe?: string;
  /** Mehrzahl von `was` für die Zählung: „arbeiten" */
  wasViele?: string;
  /** welcher es ist, in der Liste der Gruppe: der Titel des Chats */
  bezeichnung?: string;
  /** Die Leiste zeigt nur den Namen (bei mehreren mit Zahl: „Claude 2"),
   *  den Zustand trägt allein die Farbe des Punkts (Tim 19.10.2026: „Der
   *  orangene Punkt und Claude reicht. Kein extra Icon nötig." -- auch beim
   *  Warten: „Die gelbe Farbe reicht"). Der Satz bleibt in `titel`. */
  nurName?: boolean;
}

/** Ein Eintrag der Leiste: ein einzelnes Ding oder eine Gruppe. */
export interface Buendel {
  id: string;
  /** Wartendes zuerst, sonst in der Reihenfolge der Quelle */
  eintraege: Laeuft[];
  ton: Laeuft["ton"];
  titel: string;
}

const RANG: Record<Laeuft["ton"], number> = { warn: 0, laeuft: 1, leise: 2 };

/** Fasst gleichartige Einträge zusammen (Tim 17.10.2026: zweimal „Claude
 *  arbeitet" nebeneinander „kann man nicht unterscheiden"). Die Leiste
 *  zeigt je Gruppe einen Eintrag, der Klick die Liste. */
export function buendle(ls: readonly Laeuft[]): Buendel[] {
  const je = new Map<string, Laeuft[]>();
  for (const l of ls) {
    const k = l.gruppe ?? l.id;
    je.set(k, [...(je.get(k) ?? []), l]);
  }
  return [...je].map(([id, eintraege]) => {
    const sortiert = [...eintraege].sort((a, b) => RANG[a.ton] - RANG[b.ton]);
    return { id, eintraege: sortiert, ton: sortiert[0].ton, titel: sortiert.map((l) => l.titel).join("\n") };
  });
}

/** Kurzform eines Bündels: „Claude", „Claude 2", wenn jeder Eintrag
 *  `nurName` will; einzeln sonst wie kurzText („P1S 84 % · 18:52"),
 *  mehrere gezählt -- „P1S 1 druckt · 1 wartet". `ganz` gibt immer den
 *  Satz („Claude 1 wartet auf dich · 2 arbeiten"), fürs aria-label. */
export function buendelText(b: Buendel, jetzt: Date, ganz = false): string {
  const n = b.eintraege.length;
  if (!ganz && n && b.eintraege.every((l) => l.nurName)) return n === 1 ? b.eintraege[0].name : `${b.eintraege[0].name} ${n}`;
  if (n === 1) return kurzText(b.eintraege[0], jetzt);
  const zahl = new Map<string, { n: number; l: Laeuft }>();
  for (const l of b.eintraege) {
    const z = zahl.get(l.was);
    if (z) z.n++;
    else zahl.set(l.was, { n: 1, l });
  }
  const teile = [...zahl.values()].map(({ n, l }) => `${n} ${n === 1 ? l.was : (l.wasViele ?? l.was)}`);
  return `${b.eintraege[0].name} ${teile.join(" · ")}`;
}

const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Uhrzeit mit Tag, wenn es nicht heute ist: „18:52", „morgen 02:10",
 *  „Mo 09:15". Gezählt in Kalendertagen, nicht im Tag des Monats. */
function bisText(bis: Date, jetzt: Date): string {
  const hm = `${String(bis.getHours()).padStart(2, "0")}:${String(bis.getMinutes()).padStart(2, "0")}`;
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const tage = Math.round((tag(bis) - tag(jetzt)) / 86_400_000);
  if (tage <= 0) return hm;
  if (tage === 1) return `morgen ${hm}`;
  return `${WOCHENTAG[bis.getDay()]} ${hm}`;
}

/** Kurzform für Statusleiste und Telefon: „P1S 84 % · 18:52" (die Zeit
 *  ist das Ende; ohne „bis", Tim 18.10.2026: „Geht das noch kompakter?"),
 *  „P1S angehalten: Filament leer". Ein Ende am nächsten Tag heißt
 *  „morgen 02:10". `jetzt` muss die echte Uhrzeit sein (laeuftUhr
 *  in laeuft.svelte.ts) -- eine stehengebliebene von gestern macht aus
 *  jedem Ende heute ein „morgen". */
export function kurzText(l: Laeuft, jetzt: Date): string {
  if (l.ton === "warn") return `${l.name} ${l.was}`;
  const teile = [l.name];
  if (l.fortschritt != null) teile.push(`${l.fortschritt} %`);
  else teile.push(l.was);
  let s = teile.join(" ");
  if (l.bis && l.ton === "laeuft") {
    const b = new Date(l.bis);
    if (!Number.isNaN(b.getTime())) s += ` · ${bisText(b, jetzt)}`;
  }
  return s;
}

/** Was ein Tab zeigt, wenn auf seiner Seite etwas arbeitet. */
export interface TabLauf {
  ton: "laeuft" | "warn";
  titel: string;
}

/** Seitenpfad ohne Abfrage, Anker und Schrägstrich am Ende. */
export function seitenPfadVon(p: string): string {
  return p.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
}

/** Tab-Zustand je Seitenpfad (Tim 25.10.2026: „wenn in einem Tab etwas
 *  arbeitet, wie Claude, dann sollte das noch an dem Tab ersichtlich
 *  sein"). Wartet etwas auf dich, schlägt das „arbeitet": der Handgriff
 *  ist das Wichtigere. Pausiertes (leise) zeigt der Tab nicht. */
export function tabLaeufe(ls: readonly Laeuft[]): Map<string, TabLauf> {
  const m = new Map<string, TabLauf>();
  for (const l of ls) {
    if (l.ton === "leise") continue;
    for (const p of [l.pfad, ...(l.pfade ?? [])]) {
      const k = seitenPfadVon(p);
      const alt = m.get(k);
      if (!alt || (alt.ton === "laeuft" && l.ton === "warn")) m.set(k, { ton: l.ton, titel: l.titel });
    }
  }
  return m;
}
